/**
 * The paged list of schools within two miles of a property, bound to the
 * published inspection snapshot the diligence view returned.
 */
import { useQuery } from '@tanstack/react-query'
import { ApiError, api } from './http'
import type { SchoolInspectionRow } from './areaPotential'

export interface SchoolPage { propertyId: string; snapshotId: string; total: number; filteredTotal: number; offset: number; limit: number; rows: SchoolInspectionRow[] }
export interface SchoolFilters { phase: string; format: string; area?: string; grade?: string }

export const SCHOOL_PAGE_SIZE = 30

export function useSchoolPage(propertyId: string, snapshotId: string | null, filters: SchoolFilters, offset: number) {
  return useQuery({
    queryKey: ['schools', propertyId, snapshotId, filters, offset],
    queryFn: async ({ signal }): Promise<SchoolPage> => {
      const envelope = await api<{ data?: SchoolPage }>('schools', {
        params: { propertyId },
        query: { snapshotId: snapshotId ?? '', phase: filters.phase, format: filters.format, offset, limit: SCHOOL_PAGE_SIZE, area: filters.area, grade: filters.grade },
        signal,
      })
      const data = envelope?.data
      if (!data || data.snapshotId !== snapshotId || data.propertyId !== propertyId || !Array.isArray(data.rows) || !Number.isInteger(data.filteredTotal)) {
        throw new ApiError(502, 'schools_contract_mismatch', 'The school list did not match its published snapshot')
      }
      return data
    },
    enabled: snapshotId !== null,
    retry: false,
    staleTime: Infinity,
  })
}

export const schoolPhases = [{ value: 'all', label: 'All schools' }, { value: 'primary', label: 'Primary' }, { value: 'secondary', label: 'Secondary' }, { value: 'independent', label: 'Independent' }]
export const inspectionFormats = [
  { value: 'all', label: 'All inspection formats' }, { value: 'legacy_overall', label: 'Legacy overall ratings' },
  { value: 'report_card', label: 'New report cards' }, { value: 'legacy_individual', label: 'Individual grades only' },
  { value: 'historical', label: 'Historical ratings' }, { value: 'predecessor', label: 'Predecessor inspections' },
  { value: 'unavailable', label: 'Rating unavailable' }, { value: 'outside_coverage', label: 'Outside dataset coverage' },
]
export const reportAreas = [
  { value: 'teaching', label: 'Curriculum and teaching' }, { value: 'inclusion', label: 'Inclusion' },
  { value: 'achievement', label: 'Achievement' }, { value: 'attendance', label: 'Attendance and behaviour' },
  { value: 'development', label: 'Personal development and wellbeing' }, { value: 'leadership', label: 'Leadership and governance' },
  { value: 'earlyYears', label: 'Early years' }, { value: 'post16', label: 'Post-16 provision' },
]
export const reportGrades = ['Exceptional', 'Strong standard', 'Expected standard', 'Needs attention', 'Urgent improvement', 'Not applicable', 'Unavailable']
export const legacyGrades = ['Outstanding', 'Good', 'Requires improvement', 'Inadequate']
